import AXIOS from "@/axios";
import { APIResponse } from "@/types";
import { removeEmptyProps } from "@/utils/constants";

export const getTeachersDropdownService = async (query: {
  search?: string;
  school_id?: string;
}) => {
  return AXIOS.get<APIResponse<{ id: string; name: string }[]>>(
    "/base/api/v1/dp/tch/list",
    {
      params: removeEmptyProps(query),
    }
  );
};

export const getGradesDropdownService = async (query: {
  search?: string;
  schoolId?: string;
  teacherId?: string;
}) => {
  return AXIOS.get<APIResponse<{ id: string; grade: string }[]>>(
    "/base/api/v1/dp/cs/list",
    {
      params: removeEmptyProps({ ...query }),
    }
  );
};

export type AssignResourceType = "course" | "class";

/** Dropdown list filter for `/base/api/v2/dp/cs/list` (includes combined `all`) */
export type CsListDropdownType = AssignResourceType | "all";

export interface CourseAssignDropdownItem {
  id: string;
  grade: string;
  /** Present on some v2 `type=all` rows */
  type?: AssignResourceType | string;
}

export interface ClassAssignDropdownItem {
  id: string;
  name: string;
  /** Present on some v2 `type=all` rows */
  type?: AssignResourceType | string;
}

export type CsListGradeOption = {
  id: string;
  grade: string;
  /** Whether this row is a course (gradeId) or class (classId) for student list filters */
  kind: AssignResourceType;
};

function inferCsListItemKind(
  item: CourseAssignDropdownItem | ClassAssignDropdownItem
): AssignResourceType {
  const explicit = item.type?.toString().trim().toLowerCase();
  if (explicit === "class" || explicit === "course") {
    return explicit;
  }

  // Class rows use `name`; course rows use `grade`. Prefer name when both are absent of grade.
  if ("name" in item && item.name && !("grade" in item && item.grade)) {
    return "class";
  }
  return "course";
}

/** Normalize v2 cs/list rows for SchoolSelect-style `{ id, grade }` options */
export function mapCsListDropdownToGradeOptions(
  items: (CourseAssignDropdownItem | ClassAssignDropdownItem)[] | null | undefined,
  kind?: AssignResourceType
): CsListGradeOption[] {
  return (items ?? []).map((item) => {
    const label =
      "grade" in item && item.grade
        ? item.grade
        : "name" in item && item.name
          ? item.name
          : item.id;
    return {
      id: item.id,
      grade: label,
      kind: kind ?? inferCsListItemKind(item),
    };
  });
}

export const getAssignResourceDropdownV2Service = async (
  type: CsListDropdownType,
  query?: { schoolId?: string }
) => {
  return AXIOS.get<
    APIResponse<CourseAssignDropdownItem[] | ClassAssignDropdownItem[]>
  >("/base/api/v2/dp/cs/list", {
    params: removeEmptyProps({ type, schoolId: query?.schoolId }),
  });
};

async function mergeCourseAndClassDropdownOptions(query?: {
  schoolId?: string;
}): Promise<CsListGradeOption[]> {
  const [courses, classes] = await Promise.all([
    getAssignResourceDropdownV2Service("course", query),
    getAssignResourceDropdownV2Service("class", query),
  ]);
  const merged = new Map<string, CsListGradeOption>();
  for (const item of [
    ...mapCsListDropdownToGradeOptions(courses.data.data, "course"),
    ...mapCsListDropdownToGradeOptions(classes.data.data, "class"),
  ]) {
    merged.set(item.id, item);
  }
  return Array.from(merged.values());
}

/**
 * Load cs/list options for filters.
 *
 * For `type=all`, always merge separate `course` + `class` fetches so each option
 * carries a reliable `kind`. The combined `type=all` payload often uses one shape
 * for both resources, which made every selection map to `gradeId`.
 */
export async function fetchCsListDropdownOptions(
  type: CsListDropdownType,
  query?: { schoolId?: string }
): Promise<CsListGradeOption[]> {
  if (type === "all") {
    return mergeCourseAndClassDropdownOptions(query);
  }

  const { data } = await getAssignResourceDropdownV2Service(type, query);
  return mapCsListDropdownToGradeOptions(data.data, type);
}

export const getStudentsDropdownService = async (query: {
  search?: string;
  schoolId?: string;
  teacherId?: string;
  gradeId?: string;
  classId?: string;
  assignId?: string;
  subsn?: string;
}) => {
  return AXIOS.get<
    APIResponse<{ id: string; user_id: string; name: string }[]>
  >("/base/api/v1/dp/student/list", {
    params: removeEmptyProps({ ...query }),
  });
};

export const getLessonsDropdownService = async (query: {
  search?: string;
  gradeId?: string;
  classId?: string;
}) => {
  return AXIOS.get<APIResponse<{ id: string; lesson: string }[]>>(
    "/base/api/v1/dp/lesson/list",
    {
      params: removeEmptyProps({ ...query }),
    }
  );
};

export const getUnitsDropdownService = async (query: {
  search?: string;
  lessonId?: string;
}) => {
  return AXIOS.get<APIResponse<{ id: string; unit: string }[]>>(
    "/base/api/v1/dp/unit/list",
    {
      params: removeEmptyProps({ ...query }),
    }
  );
};
type S3UploadResponse = APIResponse<{
  id: string;
  file_name: string;
  s3_url: string;
  preview_url: string;
}>;

const postMultipartFile = (url: string, file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  return AXIOS.post<S3UploadResponse>(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const uploadFileService = async (file: File) =>
  postMultipartFile("/base/api/v1/s3/upload", file);

/** Class admin thumbnail upload (PNG, 16:9) */
export const uploadThumbnailService = async (file: File) =>
  postMultipartFile("/base/api/v1/s3/upload/thumbnail", file);

export const getSchoolDropdownService = async (query: {
  search?: string;
}) => {
  return AXIOS.get<APIResponse<{ id: string; name: string }[]>>(
    "/base/api/v1/dp/sch/list?isActive=true",
    {
      params: removeEmptyProps({ ...query }),
    }
  );
};